import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../utils/api';
import { toEasternTime } from '../utils/time';
import ConfirmDialog from '../components/ConfirmDialog';
import { PageHeader, Panel, StatusPill, Empty, Skeleton, Icon, useToast } from '../../components/ui';

const ALLOWED_TYPES = { 'image/jpeg': true, 'image/png': true, 'image/gif': true };
const ALLOWED_EXT = /\.(jpg|jpeg|png|gif)$/i;

function ImageCard({ img, onRemove }) {
  const neverUsed = !img.last_used_at;
  return (
    <div style={{ position: 'relative', background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 'var(--radius)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      {img._is_next && (
        <span className="ops-pill ops-pill--blue" style={{ position: 'absolute', top: 8, left: 8, zIndex: 1 }}>Next up</span>
      )}
      <div style={{ height: 150, background: 'var(--surface-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', borderBottom: '1px solid var(--line)' }}>
        {img.preview_url
          ? <img src={img.preview_url} alt={img.filename} style={{ maxHeight: 140, maxWidth: '100%', objectFit: 'contain' }} />
          : <span style={{ color: 'var(--ink-3)' }}><Icon name="file" size={28} /></span>}
      </div>
      <div style={{ padding: '10px 12px', flex: 1 }}>
        <div className="ops-cell-main" style={{ fontSize: 13, overflowWrap: 'anywhere' }}>{img.filename}</div>
        <div className="ops-cell-sub">Uploaded {toEasternTime(img.upload_date)}</div>
        <div className="ops-cell-sub">{neverUsed ? 'Never used' : `Last used ${toEasternTime(img.last_used_at)}`}</div>
        <div className="ops-cell-sub">Used {img.use_count || 0} {img.use_count === 1 ? 'time' : 'times'}</div>
      </div>
      <div style={{ padding: '0 12px 12px' }}>
        <button type="button" className="ops-btn ops-btn--danger ops-btn--sm" style={{ width: '100%', justifyContent: 'center' }} onClick={() => onRemove(img)}>
          <Icon name="trash" /> Remove
        </button>
      </div>
    </div>
  );
}

// Upload a single file to S3 through its presigned URL. XHR is used so progress can be shown.
function xhrUpload(url, file, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', file.type);
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100)); };
    xhr.onload = () => (xhr.status < 400 ? resolve() : reject(new Error(`S3 upload failed: ${xhr.status}`)));
    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.send(file);
  });
}

export default function BrandImagesPage() {
  const [showToast, toastNode] = useToast();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dragOver, setDragOver] = useState(false);
  const [uploadQueue, setUploadQueue] = useState([]); // { name, pct, done, error }
  const [pendingRemove, setPendingRemove] = useState(null);
  const fileRef = useRef();

  const isUploading = uploadQueue.some(f => !f.done && !f.error);
  const allDone = uploadQueue.length > 0 && !isUploading;
  const overallPct = uploadQueue.length === 0 ? 0
    : Math.round(uploadQueue.reduce((s, f) => s + (f.done ? 100 : f.pct), 0) / uploadQueue.length);

  const load = useCallback(async () => {
    try {
      const { items } = await api.getBrandImages();
      // The agent picks the image with the oldest "last used" date; never used sorts first.
      const sorted = [...items].sort((a, b) => (a.last_used_at || '').localeCompare(b.last_used_at || ''));
      setImages(items.map(img => ({ ...img, _is_next: sorted[0]?.image_key === img.image_key })));
    } catch (e) {
      showToast(`Could not load images: ${e.message}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { load(); }, [load]);

  const setFile = (name, patch) => setUploadQueue(q => q.map(f => (f.name === name ? { ...f, ...patch } : f)));

  async function uploadFile(file) {
    if (!ALLOWED_EXT.test(file.name) || !ALLOWED_TYPES[file.type]) {
      setFile(file.name, { error: 'Only JPEG, PNG or GIF files are accepted' });
      return;
    }
    try {
      const { upload_url, key, filename } = await api.getBrandImageUploadUrl(file.name, file.type);
      await xhrUpload(upload_url, file, (pct) => setFile(file.name, { pct }));
      await api.confirmBrandImageUpload({ key, filename, content_type: file.type });
      setFile(file.name, { pct: 100, done: true });
    } catch (e) {
      setFile(file.name, { error: e.message });
      showToast(`Upload failed: ${e.message}`, 'error');
    }
  }

  async function handleFiles(files) {
    if (!files?.length) return;
    const list = Array.from(files);
    setUploadQueue(list.map(f => ({ name: f.name, pct: 0, done: false, error: null })));
    await Promise.all(list.map(uploadFile));
    await load();
    setTimeout(() => setUploadQueue([]), 2500);
    if (fileRef.current) fileRef.current.value = '';
  }

  async function handleRemove() {
    const img = pendingRemove;
    setPendingRemove(null);
    setImages(prev => prev.filter(x => x.image_key !== img.image_key));
    try {
      await api.deleteBrandImage(img.image_key);
      showToast('Image removed');
    } catch (e) {
      showToast(`Could not remove the image: ${e.message}`, 'error');
    }
    load(); // recompute Next up, or restore the card if the delete failed
  }

  const onDrop = (e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); };

  return (
    <>
      <PageHeader
        title="Brand Images"
        subtitle="Photos the agent attaches to posts. They rotate so every image gets used before any repeats."
        meta={!loading ? `${images.length} ${images.length === 1 ? 'image' : 'images'}` : null}
        actions={(
          <button type="button" className="ops-btn ops-btn--primary" onClick={() => fileRef.current?.click()} disabled={isUploading}>
            <Icon name="upload" /> {isUploading ? 'Uploading...' : 'Upload images'}
          </button>
        )}
      />
      <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.gif" multiple style={{ display: 'none' }} onChange={e => handleFiles(e.target.files)} />

      <div className={`ops-dropzone${dragOver ? ' is-over' : ''}${isUploading ? ' is-busy' : ''}`}
        onDragOver={e => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)} onDrop={onDrop}
        onClick={() => !isUploading && fileRef.current?.click()} role="button" tabIndex={0}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileRef.current?.click(); } }}>
        <strong>{isUploading ? 'Uploading...' : 'Drop images here or click to browse'}</strong>
        JPEG, PNG or GIF. You can add several at once.
      </div>

      {uploadQueue.length > 0 && (
        <Panel title={allDone ? 'Upload complete' : `Uploading ${uploadQueue.filter(f => !f.done && !f.error).length} of ${uploadQueue.length}`}
          action={<span className="ops-help">{overallPct}%</span>} flush>
          <div className="ops-progress" style={{ maxWidth: 'none', margin: '14px 18px 6px' }}>
            <div className="ops-progress__bar" style={{ width: `${overallPct}%`, background: allDone ? 'var(--green)' : undefined }} />
          </div>
          <ul className="ops-list">
            {uploadQueue.map(f => (
              <li key={f.name}>
                <div className="ops-list__main"><strong>{f.name}</strong>{f.error && <span className="neg">{f.error}</span>}</div>
                <StatusPill status={f.error ? 'failed' : f.done ? 'completed' : 'running'} label={f.error ? 'Failed' : f.done ? 'Done' : `${f.pct}%`} />
              </li>
            ))}
          </ul>
        </Panel>
      )}

      <div className="ops-note">
        <strong>How rotation works.</strong> Each time the post generator runs it picks the image with the oldest last used date, and images that have never been used go first.
        Once every image has been used the cycle starts over. With no images uploaded, posts go out as text only.
      </div>

      <Panel title="Images" count={loading ? undefined : images.length}>
        {loading ? <Skeleton rows={4} />
          : images.length === 0 ? <Empty title="No images yet">Upload a brand image above and the agent will start attaching it to posts.</Empty> : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 14 }}>
              {images.map(img => <ImageCard key={img.image_key} img={img} onRemove={setPendingRemove} />)}
            </div>
          )}
      </Panel>

      {pendingRemove && (
        <ConfirmDialog title="Remove this image?" confirmLabel="Yes, remove" onConfirm={handleRemove} onCancel={() => setPendingRemove(null)}>
          <p style={{ margin: '0 0 8px', overflowWrap: 'anywhere' }}><strong>{pendingRemove.filename}</strong></p>
          <p style={{ margin: 0 }}>It will be taken out of the rotation. Posts that already used it are not affected. This cannot be undone.</p>
        </ConfirmDialog>
      )}
      {toastNode}
    </>
  );
}
